import dotPropImmutable from 'dot-prop-immutable';
import { Dictionary, keyBy } from 'lodash';
import { useCallback } from 'react';
import {
  MutationFunction,
  QueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import {
  Account,
  AccountAPI,
  AccountInitialData,
  AccountMeta,
  CustomDomain,
  EmailConfig,
  FtuePayload,
  UpdateAccountPayload,
} from 'api/account';
import {
  CompanyAPI,
  CompanyLogoResponse,
  CreateCompanyPayload,
  UpdateCompanyPayload,
} from 'api/company';
import { DocumentsAPI } from 'api/document/document-core';
import { FieldSectionType } from 'api/field-sections/types';
import { FieldPayload, FieldsAPI } from 'api/fields';
import { HomeAPI, HomeResponse } from 'api/home';
import { Company, ProjectId, ProjectPhaseId, User, UserType } from 'api/models';
import { PhaseAPI, UpdateTaskOrderPayload } from 'api/phase';
import {
  MembersMeta,
  Project,
  ProjectAPI,
  ProjectMergePayload,
  ProjectPhase,
  ProjectToTemplatePayload,
} from 'api/project';
import { Space, SpaceAPI } from 'api/space';
import {
  DuplicateTaskProps,
  Field,
  Task,
  TaskAPI,
  UpdateSubTaskOrderPayload,
} from 'api/tasks';
import {
  AvatarResponse,
  BulkUserCreatePayload,
  UserAPI,
  UserProfileInfo,
} from 'api/user';
import { toastService } from 'components/molecules/toast';
import { UserCreationProps } from 'contexts/project-creation';
import { FetchStatus } from 'data/core';
import { success } from 'data/current-user-profile';
import { AccountFields } from 'data/fields/hooks';
import { InvitedMembers } from 'data/members/slice';
import { saveTasks } from 'data/tasks/slice';
import { Template, TemplatesAPI } from 'data/templates/api';
import {
  ProjectPhaseOption,
  ProjectTemplatesAPI,
} from 'data/templates/projects/api';
import { ProjectTemplateApi } from 'data/templates/projects/types';
import { useIsCurrentUserPartner } from 'hooks/use-partner';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { added } from 'stores/companies';
import { ACCOUNT_META_DATA_QUERY_KEY } from './account';
import {
  getProjectDependencyMapKey,
  getProjectDetailQueryKey,
  getProjectTasksQueryKey,
  getProjectUsersLiteKey,
} from './common';
import {
  ALL_FIELDS_QUERY_KEY,
  ALL_PROJECT_CURRENT_PHASES_QUERY_KEY,
  ALL_PROJECTS_QUERY_KEY,
  ARCHIVED_PROJECTS_QUERY_KEY,
  FINANCIAL_FIELDS_QUERY_KEY,
  fiveMinutes,
  HOME_QUERY_KEY,
  oneDay,
  oneHour,
  PROJECT_STATUS_FIELD_QUERY_KEY,
  PROJECTS_META_QUERY_KEY,
  tenMinutes,
} from './constants';
import {
  PROJECT_FIELD_SECTIONS_QUERY_KEY_NEW as PROJECT_FIELD_SECTIONS_QUERY_KEY,
  TASK_FIELD_SECTIONS_QUERY_KEY,
  TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY,
} from './field-sections';
import { ALL_MEMBERS_QUERY_KEY, ALL_USERS_QUERY_KEY } from './members';
import { useHideNonStrategicPartner } from './partner';
import { PROJECT_TEMPLATE_OPTIONS_QUERY_KEY } from './templates/project-templates';

export const getTemplatesQueryKey = (): string[] => ['templates'];
export const getStatusUpdateTemplatesQueryKey = (): string[] => [
  'templates',
  'status-update',
];
export const getTemplatesDetailsQueryKey = (
  templateId: string | number,
): (string | number)[] => {
  return [...getTemplatesQueryKey(), templateId];
};
export const getTemplatesPublishQueryKey = (
  templateId: string | number,
): (string | number)[] => {
  return [...getTemplatesDetailsQueryKey(templateId), 'publish'];
};
export const getTaskTemplatesQueryKey = (): string[] => ['templates', 'task'];

export const useTemplatesQuery = (
  queryConfig: UseQueryOptions<Template[], unknown, Template[], string[]> = {},
) =>
  useQuery(getTemplatesQueryKey(), () => TemplatesAPI.getAllTemplates(), {
    staleTime: oneDay,
    ...queryConfig,
  });

export const useUserCreatedTaskTemplatesQuery = () => {
  const isCurrentUserPartner = useIsCurrentUserPartner();

  return useQuery(
    getTaskTemplatesQueryKey(),
    () => TemplatesAPI.getUserCreatedTaskTemplates(),
    { enabled: !isCurrentUserPartner },
  );
};

export const useDeleteTemplateMutation = (): UseMutationResult<
  number,
  unknown,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation<number, unknown, number>(
    (id) => {
      return TemplatesAPI.deleteById(id);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getTemplatesQueryKey());
        await queryClient.invalidateQueries(PROJECT_TEMPLATE_OPTIONS_QUERY_KEY);
      },
    },
  );
};

export const useDuplicateTemplateMutation = (): UseMutationResult<
  Template,
  unknown,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation(
    (id) => {
      return TemplatesAPI.cloneById(id);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getTemplatesQueryKey());
      },
    },
  );
};

export const getProjectMembersQueryKey = ([, projectId]: ProjectId): [
  string,
  number,
] => ['squad', projectId];

export const useProjectMembersQuery = (projectId: ProjectId, options = {}) =>
  useQuery(
    getProjectMembersQueryKey(projectId),
    () => ProjectAPI.getMembers(projectId[1]),
    {
      staleTime: oneDay,
      ...options,
    },
  );

export const getProjectSpacesQueryKey = ([, projectId]: ProjectId): [
  string,
  number,
] => ['spaces', projectId];

export const getProjectPhaseQueryKey = ([, phaseId]: ProjectPhaseId): [
  string,
  number,
] => ['phase', phaseId];

export const useProjectSpacesQuery = (
  projectId: ProjectId,
  options: UseQueryOptions<Space[], unknown, Space[], [string, number]> = {},
) =>
  useQuery({
    staleTime: 0,
    refetchOnWindowFocus: false,
    ...options,
    queryKey: getProjectSpacesQueryKey(projectId),
    queryFn: () => SpaceAPI.getSpaces(projectId[1]),
  });

export const SpaceDetailQueryKey = (spaceId: number): [string, number] => [
  'space',
  spaceId,
];

export const useSpaceDetailQuery = (
  spaceId: number,
  {
    queryKey,
    queryFn,
    ...rest
  }: UseQueryOptions<Space, unknown, Space, [string, number]> = {},
) =>
  useQuery(
    SpaceDetailQueryKey(spaceId),
    () => SpaceAPI.getSpaceDetail(spaceId),
    {
      staleTime: oneHour,
      ...rest,
    },
  );

export const useProjectsQuery = (
  options: UseQueryOptions<
    Project[],
    unknown,
    Project[],
    typeof ALL_PROJECTS_QUERY_KEY
  > = {},
): UseQueryResult<Project[]> => {
  const { isDependentEntitiesLoading } = useHideNonStrategicPartner();

  return useQuery(ALL_PROJECTS_QUERY_KEY, () => ProjectAPI.getProjectsLight(), {
    enabled: !isDependentEntitiesLoading,
    staleTime: oneHour,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options,
  });
};

export const useGetAllProjectCurrentPhases = (
  options: UseQueryOptions<
    ProjectPhase[],
    unknown,
    ProjectPhase[],
    typeof ALL_PROJECT_CURRENT_PHASES_QUERY_KEY
  > = {},
): UseQueryResult<ProjectPhase[]> => {
  return useQuery(
    ALL_PROJECT_CURRENT_PHASES_QUERY_KEY,
    () => ProjectAPI.getAllProjectCurrentPhases(),
    {
      staleTime: oneHour,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      ...options,
    },
  );
};

export const useProjectsMetaQuery = (
  options: UseQueryOptions<
    Project[],
    unknown,
    Project[],
    typeof PROJECTS_META_QUERY_KEY
  > = {},
): UseQueryResult<Project[]> => {
  return useQuery(PROJECTS_META_QUERY_KEY, () => ProjectAPI.getProjectsMeta(), {
    staleTime: tenMinutes,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    ...options,
  });
};

export const useCreateProjectToTemplateMutation = (
  projectId: number,
  options: UseMutationOptions<
    ProjectTemplateApi,
    unknown,
    ProjectToTemplatePayload
  > = {},
): UseMutationResult<ProjectTemplateApi, unknown, ProjectToTemplatePayload> => {
  return useMutation(
    (payload: ProjectToTemplatePayload) =>
      ProjectAPI.createProjectToTemplate(projectId, payload),
    {
      ...options,
    },
  );
};

export const useArchivedProjectsQuery = (
  options: UseQueryOptions<
    Project[],
    unknown,
    Project[],
    typeof ARCHIVED_PROJECTS_QUERY_KEY
  > = {},
): UseQueryResult<Project[]> => {
  return useQuery(
    ARCHIVED_PROJECTS_QUERY_KEY,
    () => ProjectAPI.getArchivedProjectList(),
    {
      staleTime: tenMinutes,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useCompanyTasksQuery = () => {
  return useQuery('company-tasks', () => HomeAPI.getMyCompanyTasks(), {
    staleTime: tenMinutes,
  });
};

export const useHomeQuery = (
  options: UseQueryOptions<
    HomeResponse,
    unknown,
    HomeResponse,
    typeof HOME_QUERY_KEY
  > = {},
): UseQueryResult<HomeResponse> => {
  return useQuery(HOME_QUERY_KEY, () => HomeAPI.getHomeScreenResponse(), {
    staleTime: tenMinutes,
    retry: 2,
    refetchOnMount: 'always',
    ...options,
  });
};

export const TASK_FIELDS_QUERY_KEY = 'task-fields';

export const useTaskFieldsQuery = (
  queryOptions: UseQueryOptions<
    AccountFields,
    unknown,
    AccountFields,
    typeof TASK_FIELDS_QUERY_KEY
  > = {},
) =>
  useQuery(
    TASK_FIELDS_QUERY_KEY,
    () => {
      return TaskAPI.getTaskFields();
    },
    { staleTime: oneDay, ...queryOptions },
  );

export const useAllFieldsQuery = (queryOptions = {}) =>
  useQuery(
    ALL_FIELDS_QUERY_KEY,
    () => {
      return FieldsAPI.getAllFields();
    },
    { staleTime: oneDay, ...queryOptions },
  );

export const useFieldsQueryFilter = (
  select: ((f: Field) => boolean)[],
): UseQueryResult<Field[], unknown> => {
  return useAllFieldsQuery({
    select: useCallback(
      (fields) =>
        fields.filter((field) => select.every((filter) => filter(field))),
      [select],
    ),
  });
};

const taskField = (field: Field): boolean =>
  field.objectType === FieldSectionType.Task;
const isCustomField = (field: Field): boolean => field.customField;
const isEnabled = (field: Field): boolean => field.isEnabled;
const customTaskFieldFilter = [taskField, isCustomField, isEnabled];

export const useCustomTaskFields = (): UseQueryResult<Field[], unknown> =>
  useFieldsQueryFilter(customTaskFieldFilter);
export const useProjectFieldsQuery = (queryOptions = {}) =>
  useQuery(
    ALL_FIELDS_QUERY_KEY,
    () => {
      return FieldsAPI.getAllFields();
    },
    {
      staleTime: oneDay,
      select: (res) => {
        return res.filter(
          ({ objectType }) => objectType === FieldSectionType.Project,
        );
      },
      ...queryOptions,
    },
  );

export const useProjectStatusFieldQuery = () => {
  const { data } = useProjectFieldsQuery();

  return useQuery(PROJECT_STATUS_FIELD_QUERY_KEY, () =>
    data?.find((field) => field.fieldName === 'Status'),
  );
};

export const useEnabledProjectFieldsQuery = (queryOptions = {}) =>
  useQuery(
    ALL_FIELDS_QUERY_KEY,
    () => {
      return FieldsAPI.getAllFields();
    },
    {
      staleTime: oneDay,
      select: (res) => {
        return res.filter(
          ({ objectType, isEnabled: isFieldEnabled }) =>
            objectType === FieldSectionType.Project && isFieldEnabled,
        );
      },
      ...queryOptions,
    },
  );

export const useTasksFieldsQuery = (queryOptions = {}) =>
  useQuery(
    ALL_FIELDS_QUERY_KEY,
    () => {
      return FieldsAPI.getAllFields();
    },
    {
      staleTime: oneDay,
      select: (res) => {
        return res.filter(
          ({ objectType }) => objectType === FieldSectionType.Task,
        );
      },
      ...queryOptions,
    },
  );

export const useTeamMemberFieldsQuery = (queryOptions = {}) =>
  useQuery(
    ALL_FIELDS_QUERY_KEY,
    () => {
      return FieldsAPI.getAllFields();
    },
    {
      staleTime: oneDay,
      select: (res) => {
        return res.filter(
          ({ objectType }) => objectType === FieldSectionType.Users,
        );
      },
      ...queryOptions,
    },
  );

export const useFinancialFieldsQuery = (
  queryOptions: UseQueryOptions<
    Dictionary<Field>,
    unknown,
    Dictionary<Field>,
    typeof FINANCIAL_FIELDS_QUERY_KEY
  > = {},
): UseQueryResult<Dictionary<Field>> =>
  useQuery(
    FINANCIAL_FIELDS_QUERY_KEY,
    () =>
      FieldsAPI.getAllFields().then((data) =>
        keyBy(
          data.filter(
            ({ objectType }) => objectType === FieldSectionType.Financial,
          ),
          'fieldName',
        ),
      ),
    {
      staleTime: oneDay,
      ...queryOptions,
    },
  );

export const useFieldMutation = (
  mutationFn: MutationFunction<Field, FieldPayload>,
  options: UseMutationOptions<Field, unknown, FieldPayload> = {},
): UseMutationResult<Field, unknown, FieldPayload> => {
  const queryClient = useQueryClient();

  return useMutation<Field, unknown, FieldPayload, unknown>(mutationFn, {
    ...options,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries(ALL_FIELDS_QUERY_KEY);
      if (data && data.objectType === FieldSectionType.Task) {
        await queryClient.invalidateQueries(TASK_FIELD_SECTIONS_QUERY_KEY);
      }

      if (data.objectType === FieldSectionType.Project) {
        await queryClient.invalidateQueries(PROJECT_FIELD_SECTIONS_QUERY_KEY);
      }

      if (data.objectType === FieldSectionType.Users) {
        await queryClient.invalidateQueries(
          TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY,
        );
      }

      if (options.onSuccess) {
        await options.onSuccess(data, variables, context);
      }
    },
  });
};

export const useCreateFieldMutation = (
  options: UseMutationOptions<Field, unknown, FieldPayload> = {},
) => {
  return useFieldMutation(
    (payload: FieldPayload) => FieldsAPI.createField(payload),
    options,
  );
};

export const useUpdateFieldMutation = (
  fieldId: number,
  options: UseMutationOptions<Field, unknown, FieldPayload> = {},
) => {
  const queryClient = useQueryClient();

  return useFieldMutation(
    (payload: FieldPayload) => FieldsAPI.updateField(fieldId, payload),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        options.onSuccess?.(data, variables, context);
        queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
      },
    },
  );
};

export const useDeleteFieldMutation = (fieldId: number) => {
  return useFieldMutation(() => FieldsAPI.deleteField(fieldId));
};

export const useGetFieldProjectMapping = (fieldId) => {
  return useMutation<Field, unknown, FieldPayload, unknown>(
    ({ value }) => FieldsAPI.getFieldProjectMapping(fieldId, value),
    {
      onSuccess: async (data) => {
        return data;
      },
    },
  );
};

const ACCOUNT_META_QUERY_KEY = ['account-meta'];

export const useAccountMetaQuery = (
  queryOptions: UseQueryOptions<
    AccountMeta,
    unknown,
    AccountMeta,
    typeof ACCOUNT_META_QUERY_KEY
  > = {},
): UseQueryResult<AccountMeta> => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const domain = searchParams.get('domain');

  return useQuery(
    ACCOUNT_META_QUERY_KEY,
    () => AccountAPI.getMeta(domain, location.pathname),
    {
      staleTime: oneDay,
      refetchOnWindowFocus: false,
      ...queryOptions,
    },
  );
};

export const useCompanyUpdateMutation = (
  companyId: number,
): UseMutationResult<Company, unknown, UpdateCompanyPayload> => {
  return useMutation<Company, unknown, UpdateCompanyPayload>((payload) =>
    CompanyAPI.updateCompany(companyId, payload),
  );
};

const PROJECT_TEMPLATE_QUERY_KEY = 'project_templates';

export const useProjectTemplates = (
  options: UseQueryOptions<
    ProjectTemplateApi[],
    unknown,
    ProjectTemplateApi[],
    typeof PROJECT_TEMPLATE_QUERY_KEY
  > = {},
): UseQueryResult<ProjectTemplateApi[]> => {
  const isCurrentUserPartner = useIsCurrentUserPartner();

  return useQuery(
    PROJECT_TEMPLATE_QUERY_KEY,
    () => {
      return ProjectTemplatesAPI.getAllTemplates();
    },
    {
      staleTime: oneDay,
      refetchOnWindowFocus: false,
      enabled: !isCurrentUserPartner,
      ...options,
    },
  );
};

const GET_PHASES_PROJECT_TEMPLATE_QUERY_KEY = 'phases_project_templates';

export const useGetPhasesProjectTemplatesQuery = (
  options: UseQueryOptions<
    ProjectPhaseOption[],
    unknown,
    ProjectPhaseOption[],
    typeof GET_PHASES_PROJECT_TEMPLATE_QUERY_KEY
  > = {},
): UseQueryResult<ProjectPhaseOption[]> => {
  return useQuery(
    GET_PHASES_PROJECT_TEMPLATE_QUERY_KEY,
    () => {
      return ProjectTemplatesAPI.getPhases();
    },
    { staleTime: fiveMinutes, refetchOnWindowFocus: false, ...options },
  );
};

export const useCreateCompanyMutation = (
  options: UseMutationOptions<Company, unknown, CreateCompanyPayload> = {},
) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<Company, unknown, CreateCompanyPayload>(
    (payload) => CompanyAPI.createCompany(payload),
    {
      onSuccess: async (company) => {
        dispatch(added(company));
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
      },
      ...options,
    },
  );
};

export const useCreateUserMutation = (
  options: UseMutationOptions<User, unknown, UserCreationProps> = {},
): UseMutationResult<User, unknown, UserCreationProps> => {
  const queryClient = useQueryClient();

  return useMutation<User, unknown, UserCreationProps>(
    (payload) => UserAPI.createUser(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
      },
      ...options,
    },
  );
};

export const useAccountUpdateMutation = (
  options: UseMutationOptions<Account, unknown, UpdateAccountPayload> = {},
): UseMutationResult<Account, unknown, UpdateAccountPayload> => {
  const queryClient = useQueryClient();

  return useMutation<Account, unknown, UpdateAccountPayload>(
    (payload) => AccountAPI.updateAccount(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
      },
      ...options,
    },
  );
};

type ProjectCustomerQueryKey = [string, number, Record<string, unknown>];
const getProjectCustomerQueryKey = (
  [, projectId]: ProjectId,
  params = {},
): ProjectCustomerQueryKey => {
  return ['project', projectId, params];
};

export const useProjectCustomerQuery = (
  projectId: ProjectId,
  options: UseQueryOptions<
    Project,
    unknown,
    Project,
    ProjectCustomerQueryKey
  > = {},
) => {
  const params = { include: 'customerId' };

  return useQuery(
    getProjectCustomerQueryKey(projectId, params),
    () => ProjectAPI.getProjectDetails(projectId[1], params),
    { staleTime: oneHour, ...options },
  );
};

export const useBulkUserCreateMutation = (
  options: UseMutationOptions<User[], unknown, BulkUserCreatePayload> = {},
) => {
  const queryClient = useQueryClient();

  return useMutation<User[], unknown, BulkUserCreatePayload>(
    (payload) => UserAPI.bulkCreate(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
      },
      ...options,
    },
  );
};

export const useAvatarUploadMutation = (
  options: UseMutationOptions<AvatarResponse, unknown, File> = {},
): UseMutationResult<AvatarResponse, unknown, File> => {
  return useMutation<AvatarResponse, unknown, File>(
    (payload) => UserAPI.uploadProfilePic(payload),
    options,
  );
};

export const useCompanyLogoUploadMutation = (
  options: UseMutationOptions<AvatarResponse, unknown, File> = {},
): UseMutationResult<AvatarResponse, unknown, File> => {
  return useMutation<AvatarResponse, unknown, File>(
    (payload) => CompanyAPI.uploadPicture(payload),
    options,
  );
};

export const useUpdateUserMetaMutation = (
  options: UseMutationOptions<UserProfileInfo, unknown, any> = {},
): UseMutationResult<UserProfileInfo, unknown, any> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<UserProfileInfo, unknown, any>(
    (payload) => UserAPI.updateMeta(payload),
    {
      onSuccess: async () => {
        /**
         * Since context provider is removed as part of perf optimization
         * https://github.com/Rocketlane/rocket-frontend/pull/6299
         * query key is not active, hence doing the following changes,
         * along with query invalidation.
         */
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY, {
          refetchInactive: true,
        });
        const accountMeta: AccountInitialData | undefined =
          queryClient.getQueryData(ACCOUNT_META_DATA_QUERY_KEY);

        if (accountMeta?.userProfile) {
          dispatch(success(accountMeta.userProfile));
        }
      },
      ...options,
    },
  );
};

export const useOptimisticUpdateUserMetaMutation = (
  options: UseMutationOptions<
    UserProfileInfo,
    unknown,
    any,
    UserProfileInfo
  > = {},
): UseMutationResult<UserProfileInfo, unknown, any> => {
  const dispatch = useDispatch();
  const usersData = useUser();

  return useMutation((payload) => UserAPI.updateMeta(payload), {
    onMutate: (payload) => {
      if (usersData && usersData?.userMeta) {
        const data = dotPropImmutable.set(
          usersData,
          'userMeta.metaData',
          payload.metaData,
        );

        dispatch(success(data));
      }

      return usersData;
    },
    onError: (error, _, previousData) => {
      if (previousData) {
        dispatch(success(previousData));
      }
    },
    ...options,
  });
};
/**
 * Note: remove this mutation (useUpdateCurrentUserMutation) and do not use it.
 * */
export const useUpdateCurrentUserMutation = (
  options: UseMutationOptions<User, unknown, Partial<User>> = {},
): UseMutationResult<User, unknown, Partial<User>> => {
  const queryClient = useQueryClient();

  return useMutation<User, unknown, Partial<User>>(
    (payload) => UserAPI.updateCurrentUser(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
      },
      ...options,
    },
  );
};

type UseCompanyLogoQueryParams = {
  companyDomain: string;
};

export const useCompanyLogoQuery = (
  params: UseCompanyLogoQueryParams,
): UseQueryResult<CompanyLogoResponse> => {
  return useQuery<
    CompanyLogoResponse,
    unknown,
    CompanyLogoResponse,
    [string, UseCompanyLogoQueryParams]
  >(
    ['company-domain', params],
    ({ queryKey }) => CompanyAPI.getCompanyLogoUrl(queryKey[1].companyDomain),
    {
      enabled: params.companyDomain.length > 0,
      staleTime: tenMinutes,
      refetchOnWindowFocus: false,
    },
  );
};

export const useCsatQuery = () => {
  const user = useUser();

  return useQuery('CSAT', () => HomeAPI.getCsat(), {
    enabled: user.user.userType === UserType.External,
    staleTime: fiveMinutes,
  });
};

export const useProjectMergeMutation = (
  projectId: ProjectId,
  options: UseMutationOptions<Project, unknown, ProjectMergePayload> = {},
): UseMutationResult<Project, unknown, ProjectMergePayload> => {
  const queryClient = useQueryClient();

  return useMutation<Project, unknown, ProjectMergePayload>(
    (payload) => ProjectAPI.merge(projectId[1], payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getProjectDetailQueryKey(projectId),
        );
        await queryClient.invalidateQueries(getProjectTasksQueryKey(projectId));
      },
      ...options,
    },
  );
};

export const getDocumentsQueryKey = ([, projectId]: ProjectId) => [
  'documents',
  projectId,
];
export const useDocumentsQuery = (projectId: ProjectId) => {
  return useQuery(
    getDocumentsQueryKey(projectId),
    () => DocumentsAPI.getDocumentsList(projectId[1]),
    {
      staleTime: tenMinutes,
    },
  );
};

export const useAddExistingTasksMutation = (
  projectId: number,
  options: UseMutationOptions<
    Task[],
    unknown,
    { tasks: Task[]; meta: any }
  > = {},
): UseMutationResult<Task[], unknown, { tasks: Task[]; meta: any }> => {
  return useMutation(
    ({ tasks, meta }) =>
      DocumentsAPI.addExistingSectionTask(projectId, tasks, meta),
    {
      ...options,
    },
  );
};

export type UseAddMembersToProjectMutationVariables = {
  projectId: ProjectId;
  payload: InvitedMembers[];
  suppressToast?: boolean;
};

export const useAddMembersToProjectMutation = (): UseMutationResult<
  InvitedMembers[],
  unknown,
  UseAddMembersToProjectMutationVariables
> => {
  const successTitle = useTranslation('invitation.team_member.success.title');
  const successMessage = useTranslation(
    'invitation.team_member.success.content',
  );
  const errorTitle = useTranslation('invitation.team_member.error.title');
  const errorMessage = useTranslation('invitation.team_member.error.content');
  const queryClient = useQueryClient();

  return useMutation<
    InvitedMembers[],
    unknown,
    UseAddMembersToProjectMutationVariables
  >(({ projectId, payload }) => ProjectAPI.addMembers(projectId[1], payload), {
    onSuccess: async (_, { projectId, suppressToast }) => {
      await queryClient.invalidateQueries(getProjectMembersQueryKey(projectId));
      await queryClient.invalidateQueries(getProjectUsersLiteKey(projectId[1]));
      if (suppressToast !== true) {
        toastService({
          title: successTitle,
          message: successMessage,
        }).success();
      }
    },
    onError: (_, { suppressToast }) => {
      if (suppressToast !== true) {
        toastService({
          title: errorTitle,
          message: errorMessage,
        }).error();
      }
    },
  });
};

export const GET_EMAIL_CONFIG = 'getEmailConfig';

export const useGetDKIMConfig = (): UseQueryResult<EmailConfig[], unknown> => {
  return useQuery(GET_EMAIL_CONFIG, () => AccountAPI.getEmailConfig(), {
    retry: 1,
    staleTime: fiveMinutes,
  });
};

export const useFTUEMutation = (): UseMutationResult<
  unknown,
  unknown,
  FtuePayload
> => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, FtuePayload>({
    mutationFn: (payload) => AccountAPI.toggleFTUE(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
      await queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
      await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
    },
  });
};

export const useCreateTaskMutation = ([
  ,
  projectId,
]: ProjectId): UseMutationResult<Task, unknown, any> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<Task, unknown, any>(
    (payload) => TaskAPI.createTask(projectId, payload),
    {
      onSuccess: async (data) => {
        dispatch(saveTasks({ data: [data], fetchStatus: FetchStatus.Loaded }));
        if (data.projectPhase) {
          await queryClient.invalidateQueries(
            getProjectDetailQueryKey(ProjectId(projectId)),
          );
        }
      },
    },
  );
};

export const useAddTaskTemplateMutation = ([
  ,
  projectId,
]: ProjectId): UseMutationResult<Task[], unknown, any> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<Task[], unknown, any>(
    (payload) => TaskAPI.addTaskTemplatesToProject(projectId, payload),
    {
      onSuccess: async (data) => {
        dispatch(saveTasks({ data, fetchStatus: FetchStatus.Loaded }));
        if (data[0]?.projectPhase) {
          await queryClient.invalidateQueries(
            getProjectDetailQueryKey(ProjectId(projectId)),
          );
        }
      },
    },
  );
};

export const useDuplicateTaskMutation = ([
  ,
  projectId,
]: ProjectId): UseMutationResult<void, unknown, DuplicateTaskProps> => {
  const duplicationInProgress = useTranslation(
    'task.duplicate_task.in_progress',
  );
  const notifyDuplication = useTranslation('task.duplicate_task.notify');
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, DuplicateTaskProps>(
    (payload) => TaskAPI.duplicateTask(projectId, payload),
    {
      onSuccess: async () => {
        toastService({
          title: duplicationInProgress,
          message: notifyDuplication,
        }).success();
        const data = await TaskAPI.getTaskList(projectId);

        queryClient.refetchQueries(
          getProjectDetailQueryKey(ProjectId(projectId)),
        );
        dispatch(saveTasks({ data, fetchStatus: FetchStatus.Loaded }));

        await queryClient.invalidateQueries(
          getProjectDependencyMapKey(projectId),
        );
      },
    },
  );
};

export const useCreateProjectTaskMutation = (): UseMutationResult<
  Task,
  unknown,
  any
> => {
  return useMutation<
    Task,
    unknown,
    { projectId: number; payload: { taskName: string } }
  >(({ projectId, payload }) => TaskAPI.createTask(projectId, payload));
};

/**
 * Custom domain queries
 */

export const GET_CUSTOM_DOMAIN_QUERY_KEY = 'getCustomDomainQuery';

export const useCustomDomainConfigQuery = (): UseQueryResult<
  CustomDomain[],
  unknown
> => {
  return useQuery(
    GET_CUSTOM_DOMAIN_QUERY_KEY,
    () => AccountAPI.getCustomDomain(),
    {
      retry: 1,
      staleTime: fiveMinutes,
      refetchOnMount: true,
    },
  );
};

export const useUpdateTaskOrderOfPhaseMutation = (
  phaseId: number,
): UseMutationResult<null, unknown, UpdateTaskOrderPayload> => {
  return useMutation<null, unknown, UpdateTaskOrderPayload>((payload) =>
    PhaseAPI.updateTaskOrder(phaseId, payload),
  );
};

export const useGetProjectPhaseQuery = (
  phaseId: ProjectPhaseId,
): UseQueryResult<ProjectPhase> => {
  return useQuery(getProjectPhaseQueryKey(phaseId), () =>
    PhaseAPI.getProjectPhase(phaseId[1]),
  );
};

export const useUpdateSubTaskOrderMutation = (
  taskId: number,
): UseMutationResult<void, unknown, UpdateSubTaskOrderPayload> => {
  return useMutation<void, unknown, UpdateSubTaskOrderPayload>((payload) =>
    TaskAPI.updateSubTaskOrder(taskId, payload),
  );
};

export const useProjectMembersMetaQuery = (
  [, projectId]: ProjectId,
  options: UseQueryOptions<
    MembersMeta,
    unknown,
    MembersMeta,
    [string, number]
  > = {},
): UseQueryResult<MembersMeta> => {
  return useQuery(
    ['members-meta', projectId],
    () => ProjectAPI.getProjectMembersMeta(projectId),
    options,
  );
};

// @TODO commented as part of removing get-members api. Remove this hook after confirming that there is no impact
/* export const useAllProjectMembershipsQuery = (): UseQueryResult<
  ProjectMembership[]
> => {
  const dispatch = useDispatch();

  return useQuery<ProjectMembership[]>(
    'ALL_PROJECT_MEMBERSHIPS',
    () => MembersAPI.getAllProjectMemberships(),
    {
      onSuccess: (memberships) => {
        dispatch(
          setLoadedSeveral(
            memberships.map(({ projectId, members, teamList }) => ({
              projectId: ProjectId(projectId),
              memberships: members.map(
                ({
                  user: { userId },
                  permissionSet: { permissionSetType: memberType },
                  joinedAt,
                }) => ({
                  userId: UserId(userId),
                  memberType,
                  joinedAt,
                }),
              ),
              placeholders: teamList,
            })),
          ),
        );
      },
    },
  );
}; */

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});
